import { useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  ChevronDown,
  ChevronRight,
  CircleCheck,
  CircleSlash,
  FileText,
  FlaskConical,
  Send,
  Sliders,
} from "lucide-react";

import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Skeleton } from "@/components/ui/Skeleton";
import { EmptyState } from "@/components/ui/EmptyState";
import { useToast } from "@/components/ui/Toast";
import {
  Input,
  Textarea,
  SegmentedControl,
  Slider,
} from "@/components/ui/Input";
import { useShell } from "@/components/Shell";
import {
  SettingsLayout,
  FormSection,
  FormRow,
} from "@/routes/Settings/SettingsLayout";
import {
  getOverview,
  sandboxChat,
  updateSettings,
  type Campground,
  type SandboxChatResponse,
  type UpdateSettingsInput,
  type WidgetConfig,
} from "@/lib/api";
import { cn } from "@/lib/cn";

/**
 * Settings → Bot.
 *
 * One self-contained page that owns:
 *  - Personality preset + system prompt
 *  - Welcome message (widget_config.welcome_message)
 *  - Out-of-scope fallback (widget_config.fallback_mode + fallback_message)
 *  - Advanced model knobs (temperature, top_k, threshold)
 *  - A test sandbox that calls sandbox_chat with the live (unsaved) draft
 *
 * State model: load the saved Campground on mount into a `draft` object.
 * Every input edits `draft`; "Save changes" diffs draft vs saved and sends
 * only the fields that actually changed. After save, draft and saved are
 * reconciled from the server response so the dirty bar disappears.
 */

type PersonalityPreset = "friendly" | "professional" | "concise" | "custom";
type FallbackMode = "contact" | "idk" | "custom";

const FALLBACK_CONTACT = "If you need more details, please contact the campground directly.";
const FALLBACK_IDK = "I don't have that information.";

function personalityTemplate(preset: PersonalityPreset, campgroundName: string): string {
  switch (preset) {
    case "friendly":
      return `You are the warm, welcoming assistant for ${campgroundName}. Greet visitors like neighbors, keep your tone upbeat and conversational, and use plain language. Always ground your answers in the campground's knowledge base, and when something isn't covered there, say so kindly and point the visitor toward contacting the campground directly.`;
    case "professional":
      return `You are the official assistant for ${campgroundName}. Reply with clear, precise, businesslike language and avoid slang. Stick strictly to information available in the knowledge base, cite policies when relevant, and if a question falls outside what you know, state that plainly and recommend the visitor reach out to the campground for confirmation.`;
    case "concise":
      return `You are the assistant for ${campgroundName}. Keep answers as short as possible while remaining complete, prefer bullet points over prose, and never repeat the visitor's question back to them. If the knowledge base doesn't cover a question, say "I don't have that information" in one line.`;
    case "custom":
    default:
      return `You are the assistant for ${campgroundName}.`;
  }
}

interface Draft {
  personality_preset: PersonalityPreset;
  system_prompt: string;
  welcome_message: string;
  fallback_mode: FallbackMode;
  fallback_message: string;
  temperature: number;
  retrieval_top_k: number;
  retrieval_threshold: number;
}

function draftFromCampground(c: Campground): Draft {
  const wc = (c.widget_config ?? {}) as WidgetConfig;
  const preset = (wc.personality_preset ?? "custom") as PersonalityPreset;
  const fallbackMode = (wc.fallback_mode ?? "contact") as FallbackMode;
  const defaultFallback =
    fallbackMode === "idk" ? FALLBACK_IDK :
    fallbackMode === "contact" ? FALLBACK_CONTACT :
    "";
  return {
    personality_preset: preset,
    system_prompt: c.system_prompt ?? "",
    welcome_message: wc.welcome_message ?? "",
    fallback_mode: fallbackMode,
    fallback_message: wc.fallback_message ?? defaultFallback,
    temperature: typeof c.temperature === "number" ? c.temperature : 0.3,
    retrieval_top_k: typeof c.retrieval_top_k === "number" ? c.retrieval_top_k : 5,
    retrieval_threshold: typeof c.retrieval_threshold === "number" ? c.retrieval_threshold : 0.35,
  };
}

export function Bot() {
  const toast = useToast();
  const { refresh: refreshShell } = useShell();

  const [saved, setSaved] = useState<Campground | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  // Sandbox state — local to this page, never persisted.
  const [sandboxMessage, setSandboxMessage] = useState("");
  const [sandboxSending, setSandboxSending] = useState(false);
  const [sandboxResult, setSandboxResult] = useState<SandboxChatResponse | null>(null);
  const [sandboxError, setSandboxError] = useState<string | null>(null);
  const [citationsOpen, setCitationsOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await getOverview();
      setSaved(r.campground);
      setDraft(draftFromCampground(r.campground));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  // -------------------------------------------------------------------------
  // Dirty diff: produce the UpdateSettingsInput payload of *only* changed
  // fields. widget_config is merged with the existing widget_config so we
  // don't clobber keys owned by other settings pages (Widget customizer).
  // -------------------------------------------------------------------------
  const dirtyPayload: UpdateSettingsInput | null = useMemo(() => {
    if (!saved || !draft) return null;

    const payload: UpdateSettingsInput = {};
    const existingWc = (saved.widget_config ?? {}) as WidgetConfig & Record<string, unknown>;

    if ((saved.system_prompt ?? "") !== draft.system_prompt) {
      payload.system_prompt = draft.system_prompt;
    }
    if ((saved.temperature ?? 0.3) !== draft.temperature) {
      payload.temperature = draft.temperature;
    }
    if ((saved.retrieval_top_k ?? 5) !== draft.retrieval_top_k) {
      payload.retrieval_top_k = draft.retrieval_top_k;
    }
    if ((saved.retrieval_threshold ?? 0.35) !== draft.retrieval_threshold) {
      payload.retrieval_threshold = draft.retrieval_threshold;
    }

    // widget_config sub-diff.
    const wcPatch: Partial<WidgetConfig> = {};
    if ((existingWc.personality_preset ?? "custom") !== draft.personality_preset) {
      wcPatch.personality_preset = draft.personality_preset;
    }
    if ((existingWc.welcome_message ?? "") !== draft.welcome_message) {
      wcPatch.welcome_message = draft.welcome_message;
    }
    if ((existingWc.fallback_mode ?? "contact") !== draft.fallback_mode) {
      wcPatch.fallback_mode = draft.fallback_mode;
    }
    if ((existingWc.fallback_message ?? "") !== draft.fallback_message) {
      wcPatch.fallback_message = draft.fallback_message;
    }

    if (Object.keys(wcPatch).length > 0) {
      // Merge with the existing widget_config so we don't drop other keys.
      payload.widget_config = { ...existingWc, ...wcPatch };
    }

    if (Object.keys(payload).length === 0) return null;
    return payload;
  }, [saved, draft]);

  const isDirty = dirtyPayload !== null;

  const onDiscard = () => {
    if (!saved) return;
    setDraft(draftFromCampground(saved));
  };

  const onSave = async () => {
    if (!dirtyPayload) return;
    setSaving(true);
    try {
      const r = await updateSettings(dirtyPayload);
      setSaved(r.campground);
      setDraft(draftFromCampground(r.campground));
      toast.push("Bot settings saved.");
      void refreshShell();
    } catch (e) {
      toast.push(e instanceof Error ? e.message : "Couldn't save", "error");
    } finally {
      setSaving(false);
    }
  };

  // -------------------------------------------------------------------------
  // Personality handlers
  // -------------------------------------------------------------------------
  const setPersonality = (next: PersonalityPreset) => {
    if (!draft || !saved) return;
    if (next === "custom") {
      // Switching to custom keeps the current system_prompt as-is so the
      // owner can hand-tune what's already there.
      setDraft({ ...draft, personality_preset: "custom" });
      return;
    }
    // Picking a real preset overwrites the prompt with a templated one.
    setDraft({
      ...draft,
      personality_preset: next,
      system_prompt: personalityTemplate(next, saved.name),
    });
  };

  // -------------------------------------------------------------------------
  // Fallback handlers
  // -------------------------------------------------------------------------
  const setFallback = (next: FallbackMode) => {
    if (!draft) return;
    if (next === "contact") {
      setDraft({ ...draft, fallback_mode: "contact", fallback_message: FALLBACK_CONTACT });
    } else if (next === "idk") {
      setDraft({ ...draft, fallback_mode: "idk", fallback_message: FALLBACK_IDK });
    } else {
      // Preserve whatever they typed if it was custom-ish, otherwise blank.
      const carry =
        draft.fallback_mode === "custom"
          ? draft.fallback_message
          : draft.fallback_message === FALLBACK_CONTACT || draft.fallback_message === FALLBACK_IDK
            ? ""
            : draft.fallback_message;
      setDraft({ ...draft, fallback_mode: "custom", fallback_message: carry });
    }
  };

  // -------------------------------------------------------------------------
  // Sandbox
  // -------------------------------------------------------------------------
  const onSandboxSend = async () => {
    if (!draft) return;
    const msg = sandboxMessage.trim();
    if (!msg) return;
    setSandboxSending(true);
    setSandboxError(null);
    setSandboxResult(null);
    setCitationsOpen(false);
    try {
      const r = await sandboxChat({
        message: msg,
        system_prompt: draft.system_prompt,
        temperature: draft.temperature,
        retrieval_top_k: draft.retrieval_top_k,
        retrieval_threshold: draft.retrieval_threshold,
      });
      setSandboxResult(r);
    } catch (e) {
      setSandboxError(e instanceof Error ? e.message : String(e));
    } finally {
      setSandboxSending(false);
    }
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  if (loading && !draft) {
    return (
      <SettingsLayout
        title="Bot configuration"
        description="Personality, welcome message, fallback behavior, and the model knobs that drive your chatbot."
      >
        <BotPageSkeleton />
      </SettingsLayout>
    );
  }

  if (error && !draft) {
    return (
      <SettingsLayout
        title="Bot configuration"
        description="Personality, welcome message, fallback behavior, and the model knobs that drive your chatbot."
      >
        <Card className="border-danger/30">
          <div className="flex items-start gap-3 px-5 py-4">
            <AlertCircle size={18} strokeWidth={1.75} className="mt-0.5 shrink-0 text-danger" />
            <div className="flex-1">
              <p className="text-sm font-medium text-text-strong">Couldn't load bot settings</p>
              <p className="mt-1 text-xs text-text-soft">{error}</p>
            </div>
            <Button variant="secondary" size="sm" onClick={() => void load()}>
              Try again
            </Button>
          </div>
        </Card>
      </SettingsLayout>
    );
  }

  if (!draft || !saved) return null;

  const saveBar = isDirty ? (
    <>
      <div className="flex items-center gap-3">
        <span className="text-sm text-text-soft">Unsaved changes</span>
        <Button variant="ghost" size="sm" onClick={onDiscard} disabled={saving}>
          Discard
        </Button>
      </div>
      <Button variant="primary" size="md" onClick={() => void onSave()} loading={saving}>
        {saving ? "Saving…" : "Save changes"}
      </Button>
    </>
  ) : null;

  return (
    <SettingsLayout
      title="Bot configuration"
      description="Personality, welcome message, fallback behavior, and the model knobs that drive your chatbot."
      saveBar={saveBar}
    >
      {/* ------------------------------------------------------------------ */}
      {/* Personality                                                         */}
      {/* ------------------------------------------------------------------ */}
      <FormSection
        title="Personality"
        description="Choose a preset for tone, or hand-tune the full system prompt under Custom."
      >
        <FormRow
          label="Preset"
          hint="Picking a preset overwrites the system prompt below with a template tailored to your campground."
        >
          <SegmentedControl<PersonalityPreset>
            value={draft.personality_preset}
            onChange={setPersonality}
            options={[
              { value: "friendly", label: "Friendly" },
              { value: "professional", label: "Professional" },
              { value: "concise", label: "Concise" },
              { value: "custom", label: "Custom" },
            ]}
          />
        </FormRow>

        <FormRow
          label="System prompt"
          hint={
            draft.personality_preset === "custom"
              ? "Full control. This is the instruction your model sees on every turn."
              : "Generated from the preset. Switch to Custom to edit by hand."
          }
          layout="stacked"
        >
          <Textarea
            rows={8}
            value={draft.system_prompt}
            onChange={(e) =>
              setDraft({
                ...draft,
                system_prompt: e.target.value,
                // Editing the prompt directly puts us in custom mode.
                personality_preset:
                  draft.personality_preset === "custom"
                    ? "custom"
                    : e.target.value === personalityTemplate(draft.personality_preset, saved.name)
                      ? draft.personality_preset
                      : "custom",
              })
            }
            placeholder="Describe the bot's tone, what it knows, and how it should handle questions outside its knowledge base."
          />
        </FormRow>
      </FormSection>

      {/* ------------------------------------------------------------------ */}
      {/* Welcome message                                                     */}
      {/* ------------------------------------------------------------------ */}
      <FormSection title="Welcome message">
        <FormRow
          label="Welcome message"
          hint="First message the visitor sees when they open the chat."
        >
          <Input
            value={draft.welcome_message}
            onChange={(e) => setDraft({ ...draft, welcome_message: e.target.value })}
            placeholder={`Hi! Ask me anything about ${saved.name}.`}
            maxLength={240}
          />
        </FormRow>
      </FormSection>

      {/* ------------------------------------------------------------------ */}
      {/* Out-of-scope fallback                                               */}
      {/* ------------------------------------------------------------------ */}
      <FormSection
        title="Out-of-scope fallback"
        description="What the bot says when retrieval comes back empty."
      >
        <FormRow label="Behavior">
          <SegmentedControl<FallbackMode>
            value={draft.fallback_mode}
            onChange={setFallback}
            options={[
              { value: "contact", label: "Contact us" },
              { value: "idk", label: "I don't know" },
              { value: "custom", label: "Custom" },
            ]}
          />
        </FormRow>

        {draft.fallback_mode === "custom" && (
          <FormRow
            label="Custom message"
            hint="Used verbatim whenever the bot can't find a grounded answer."
          >
            <Input
              value={draft.fallback_message}
              onChange={(e) => setDraft({ ...draft, fallback_message: e.target.value })}
              placeholder="Sorry, I'm not sure about that — try asking us directly."
              maxLength={300}
            />
          </FormRow>
        )}

        {draft.fallback_mode !== "custom" && (
          <FormRow label="Preview">
            <p className="rounded border border-line bg-surface-2 px-3 py-2 text-sm text-text">
              &ldquo;{draft.fallback_message}&rdquo;
            </p>
          </FormRow>
        )}
      </FormSection>

      {/* ------------------------------------------------------------------ */}
      {/* Advanced (collapsible)                                              */}
      {/* ------------------------------------------------------------------ */}
      <section className="card">
        <button
          type="button"
          onClick={() => setAdvancedOpen((v) => !v)}
          aria-expanded={advancedOpen}
          className="flex w-full items-center justify-between gap-3 border-b border-line px-5 py-4 text-left"
        >
          <div className="flex items-center gap-2">
            <Sliders size={14} strokeWidth={1.75} className="text-text-soft" />
            <h2 className="text-md font-medium text-text-strong">Advanced</h2>
            <span className="text-xs text-text-soft">Model and retrieval tuning</span>
          </div>
          {advancedOpen ? (
            <ChevronDown size={16} strokeWidth={1.75} className="text-text-soft" />
          ) : (
            <ChevronRight size={16} strokeWidth={1.75} className="text-text-soft" />
          )}
        </button>

        {advancedOpen && (
          <div className="divide-y divide-line">
            <FormRow
              label="Temperature"
              hint="Lower = more grounded and predictable. Higher = more creative."
            >
              <Slider
                value={draft.temperature}
                onChange={(v) => setDraft({ ...draft, temperature: v })}
                min={0}
                max={2}
                step={0.05}
              />
            </FormRow>
            <FormRow
              label="Top K"
              hint="How many knowledge chunks the bot sees per question."
            >
              <Slider
                value={draft.retrieval_top_k}
                onChange={(v) => setDraft({ ...draft, retrieval_top_k: Math.round(v) })}
                min={1}
                max={20}
                step={1}
                unit="chunks"
              />
            </FormRow>
            <FormRow
              label="Threshold"
              hint="Minimum similarity for a chunk to be used. Higher = the bot says 'I don't know' more often."
            >
              <Slider
                value={draft.retrieval_threshold}
                onChange={(v) => setDraft({ ...draft, retrieval_threshold: v })}
                min={0}
                max={1}
                step={0.05}
              />
            </FormRow>
          </div>
        )}
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* Test sandbox                                                        */}
      {/* ------------------------------------------------------------------ */}
      <FormSection
        title="Test sandbox"
        description="Try a question with your current (unsaved) settings. Nothing here is logged or saved."
      >
        <FormRow label="Sandbox" layout="stacked">
          <div className="space-y-3">
            <Textarea
              rows={2}
              value={sandboxMessage}
              onChange={(e) => setSandboxMessage(e.target.value)}
              placeholder="What time is check-in?"
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  void onSandboxSend();
                }
              }}
            />
            <div className="flex items-center justify-between">
              <p className="text-2xs text-text-faint">
                Cmd/Ctrl + Enter to send. Uses your live draft, not the saved version.
              </p>
              <Button
                variant="primary"
                size="sm"
                onClick={() => void onSandboxSend()}
                loading={sandboxSending}
                disabled={!sandboxMessage.trim()}
                iconLeft={!sandboxSending && <Send size={12} strokeWidth={1.75} />}
              >
                {sandboxSending ? "Asking…" : "Send"}
              </Button>
            </div>

            {/* Result / empty / error */}
            {sandboxError && (
              <div className="rounded border border-danger/30 bg-surface-2 px-3 py-2.5">
                <div className="flex items-start gap-2">
                  <AlertCircle size={14} strokeWidth={1.75} className="mt-0.5 shrink-0 text-danger" />
                  <p className="text-xs text-danger">{sandboxError}</p>
                </div>
              </div>
            )}

            {!sandboxError && !sandboxResult && !sandboxSending && (
              <div className="rounded border border-line bg-surface-2 px-4 py-4">
                <EmptyState
                  icon={FlaskConical}
                  title="No preview yet"
                  description="Type a question to preview how your bot responds with these settings."
                />
              </div>
            )}

            {sandboxResult && (
              <div className="rounded border border-line bg-surface-2">
                <div className="px-4 py-3">
                  <div className="mb-2 flex flex-wrap items-center gap-2">
                    <GroundedBadge grounded={sandboxResult.grounded} />
                    <span className="text-2xs text-text-faint">
                      {sandboxResult.model_used} · {sandboxResult.latency_ms} ms ·{" "}
                      {sandboxResult.input_tokens} in / {sandboxResult.output_tokens} out
                    </span>
                  </div>
                  <p className="whitespace-pre-wrap text-sm leading-relaxed text-text-strong">
                    {sandboxResult.answer}
                  </p>
                </div>

                {sandboxResult.citations.length > 0 && (
                  <div className="border-t border-line">
                    <button
                      type="button"
                      onClick={() => setCitationsOpen((v) => !v)}
                      aria-expanded={citationsOpen}
                      className="flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left text-xs text-text-soft hover:text-text-strong"
                    >
                      <span className="inline-flex items-center gap-1.5">
                        <FileText size={12} strokeWidth={1.75} />
                        Citations ({sandboxResult.citations.length})
                      </span>
                      {citationsOpen ? (
                        <ChevronDown size={14} strokeWidth={1.75} />
                      ) : (
                        <ChevronRight size={14} strokeWidth={1.75} />
                      )}
                    </button>
                    {citationsOpen && (
                      <ul className="divide-y divide-line border-t border-line">
                        {sandboxResult.citations.map((c, i) => (
                          <li
                            key={`${c.document_id}-${i}`}
                            className="flex items-center justify-between gap-3 px-4 py-2 text-xs"
                          >
                            <span className="min-w-0 truncate text-text">{c.title}</span>
                            <span className="shrink-0 tabular-nums text-text-soft">
                              {c.similarity.toFixed(3)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </FormRow>
      </FormSection>
    </SettingsLayout>
  );
}

// ---------------------------------------------------------------------------
// Tiny pieces
// ---------------------------------------------------------------------------

function GroundedBadge({ grounded }: { grounded: boolean }) {
  const Icon = grounded ? CircleCheck : CircleSlash;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-2xs",
        grounded
          ? "border-success/30 text-success"
          : "border-warning/30 text-warning",
      )}
      title={
        grounded
          ? "The answer used retrieved knowledge from your campground."
          : "Retrieval returned no usable chunks. The bot is falling back."
      }
    >
      <Icon size={11} strokeWidth={1.75} />
      {grounded ? "Grounded" : "Not grounded"}
    </span>
  );
}

function BotPageSkeleton() {
  return (
    <div className="space-y-6">
      {[0, 1, 2, 3].map((i) => (
        <section key={i} className="card">
          <div className="border-b border-line px-5 py-4">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="mt-2 h-3 w-72" />
          </div>
          <div className="space-y-4 px-5 py-5">
            <Skeleton className="h-9 w-64" />
            <Skeleton className="h-24 w-full" />
          </div>
        </section>
      ))}
    </div>
  );
}
